(() => {
  const ROOT_SELECTOR = '[data-lnp-bundle-upgrade]';
  const FORM_SELECTOR = '[data-lnp-bundle-form]';
  const SOURCE = 'lnp-bundle-upgrade';

  const analyticsPayload = (root, extra = {}) => ({
    surface: 'single_pdp',
    current_style: root.dataset.currentStyleName,
    product_id: numberValue(root.dataset.lnProductId) || undefined,
    product_handle: root.dataset.lnProductHandle,
    product_title: root.dataset.lnProductTitle,
    bundle_id: numberValue(root.dataset.lnBundleId) || undefined,
    bundle_handle: root.dataset.lnBundleHandle,
    bundle_title: root.dataset.bundleTitle,
    variant_id: numberValue(root.dataset.bundleVariantId) || undefined,
    value: Number(root.dataset.lnValue) || undefined,
    currency: root.dataset.lnCurrency,
    pairing: root.dataset.lnPairing,
    ...extra,
  });

  const trackAnalytics = (name, root, extra = {}) => {
    try {
      window.LashNovaAnalytics?.track?.(name, analyticsPayload(root, extra));
    } catch (_error) {
      // Analytics must never interrupt the upgrade transaction.
    }
  };

  const analyticsErrorCode = (error) => {
    try {
      return window.LashNovaAnalytics?.classifyError?.(error) || 'cart_update_failed';
    } catch (_analyticsError) {
      return 'cart_update_failed';
    }
  };

  const numberValue = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
  };

  const fetchCart = () => window.LashNovaCartTransactions.readCart();
  const standaloneItemForVariant = (cart, variantId) => window.LashNovaCartTransactions.findPlainSingle(cart, variantId);

  const getCartSurface = () =>
    document.querySelector('cart-notification') || document.querySelector('cart-drawer');

  const getSections = (cartSurface) => {
    if (!cartSurface?.getSectionsToRender) return ['cart-drawer', 'cart-icon-bubble'];
    return cartSurface
      .getSectionsToRender()
      .map((section) => section.id)
      .filter(Boolean)
      .slice(0, 5);
  };

  const publishAndRender = async (cartData, trigger, notificationOptions = {}) => {
    const cartSurface = getCartSurface();

    if (!cartSurface) {
      window.location.assign(window.routes.cart_url);
      return;
    }

    cartSurface.setActiveElement?.(trigger);
    cartSurface.classList.remove('is-empty');

    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      try {
        Promise.resolve(publish(PUB_SUB_EVENTS.cartUpdate, {
          source: SOURCE,
          cartData,
        })).catch((subscriberError) => console.error('Cart update subscriber failed:', subscriberError));
      } catch (subscriberError) {
        // The cart response is authoritative. Optional subscribers (quantity
        // rules, recommendations, analytics) must not suppress the drawer.
        console.error('Cart update subscriber failed:', subscriberError);
      }
    }

    try {
      cartSurface.renderContents(cartData, notificationOptions);
    } catch (renderError) {
      console.error('Cart drawer rendering failed:', renderError);
      window.location.assign(window.routes.cart_url);
    }
  };

  const createController = (root) => {
    if (root.dataset.lnpBundleReady === 'true') return;
    root.dataset.lnpBundleReady = 'true';

    const form = root.querySelector(FORM_SELECTOR);
    const submitButton = root.querySelector('[data-lnp-bundle-submit]');
    const actionLabel = root.querySelector('[data-lnp-bundle-action-label]');
    const upgradeNote = root.querySelector('[data-lnp-bundle-upgrade-note]');
    const errorWrapper = root.querySelector('[data-lnp-bundle-error]');
    const errorMessage = errorWrapper?.querySelector('.product-form__error-message');
    const liveStatus = root.querySelector('[data-lnp-bundle-live-status]');
    const actions = root.querySelector('.lnp-bundle-upgrade__actions');

    if (!form || !submitButton || !actionLabel) return;

    let busy = false;
    let syncRequest = 0;

    const currentVariantId = () => {
      const productInfo = root.closest('product-info');
      const mainVariantInput = productInfo?.querySelector('.lnp-product-form input[name="id"]');
      return numberValue(mainVariantInput?.value || root.dataset.currentVariantId);
    };

    const bundleVariantId = () => numberValue(root.dataset.bundleVariantId);
    const bundleTitle = root.dataset.bundleTitle || 'Bundle';
    const currentStyleName = root.dataset.currentStyleName || 'this style';

    const setError = (message = '') => {
      if (!errorWrapper || !errorMessage) return;
      errorMessage.textContent = message;
      errorWrapper.toggleAttribute('hidden', !message);
    };

    const setLiveStatus = (message = '') => {
      if (liveStatus) liveStatus.textContent = message;
    };

    const setMode = (mode) => {
      const isUpgrade = mode === 'upgrade';
      root.dataset.lnpBundleMode = mode;
      actionLabel.textContent = `${isUpgrade ? 'Upgrade to' : 'Add'} ${bundleTitle}`;
      upgradeNote?.toggleAttribute('hidden', !isUpgrade);
      submitButton.setAttribute(
        'aria-label',
        `${isUpgrade ? `Upgrade ${currentStyleName} to` : 'Add'} ${bundleTitle}, ${root.dataset.bundlePrice}`
      );
    };

    const setBusy = (value) => {
      busy = value;
      submitButton.toggleAttribute('disabled', value);
      submitButton.setAttribute('aria-disabled', String(value));
      submitButton.classList.toggle('loading', value);
      actions?.classList.toggle('is-checking', value);
      submitButton.querySelector('.loading__spinner')?.classList.toggle('hidden', !value);
    };

    const syncMode = async () => {
      const requestId = ++syncRequest;
      try {
        const cart = await fetchCart();
        if (requestId !== syncRequest || !root.isConnected) return;
        setMode(standaloneItemForVariant(cart, currentVariantId()) ? 'upgrade' : 'add');
      } catch (_error) {
        if (requestId === syncRequest) setMode('add');
      }
    };

    root.lnpSyncBundleMode = syncMode;

    root.addEventListener(
      'submit',
      async (event) => {
        if (event.target !== form) return;

        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        if (busy) return;

        const transactions = window.LashNovaCartTransactions;
        if (!transactions || transactions.isBusy()) {
          setError(transactions?.text('busy') || window.cartStrings?.error || '');
          return;
        }
        const intendedMode = root.dataset.lnpBundleMode === 'upgrade' ? 'upgrade' : 'add';
        trackAnalytics('ln_bundle_offer_click', root, { mode: intendedMode });
        const currentId = currentVariantId();
        const bundleId = bundleVariantId();
        if (!currentId || !bundleId) {
          setError(transactions.text('invalid_state'));
          return;
        }
        setError();
        setLiveStatus();
        setBusy(true);
        const sections = getSections(getCartSurface());
        try {
          const outcome = await transactions.executeBundleUpgrade({
            bundleVariantId: bundleId,
            sourceVariantId: currentId,
            sections,
            sectionsUrl: window.location.pathname,
            onResult: async (result) => {
              if (!result.verified || !result.cart) return;
              await publishAndRender(result.cart, submitButton, {
                itemKey: result.itemKey,
                variantId: result.itemKey ? bundleId : undefined,
                message: transactions.text(result.messageKey),
                success: result.status === 'added' || result.status === 'upgraded',
                fallbackMessage: transactions.text('review_needed'),
              });
            },
          });
          const success = outcome.status === 'added' || outcome.status === 'upgraded';
          trackAnalytics(success ? (outcome.status === 'upgraded' ? 'ln_bundle_upgrade_success' : 'ln_bundle_add_success') : (intendedMode === 'upgrade' ? 'ln_bundle_upgrade_error' : 'ln_bundle_add_error'), root, {
            mode: outcome.sourceCount ? 'upgrade' : 'add',
            quantity: success ? 1 : undefined,
            replaced_quantity: outcome.status === 'upgraded' ? outcome.sourceCount : undefined,
            error_code: success ? undefined : analyticsErrorCode(outcome.error),
            final_state: outcome.status === 'upgraded' ? 'bundle_replaced_single' : outcome.status === 'added' ? 'bundle_added' : outcome.status === 'review' ? 'review_required' : 'verified_singles_not_replaced',
          });
          if (success) setLiveStatus(transactions.text(outcome.messageKey));
          else setError(transactions.text(outcome.messageKey));
          if (!outcome.verified && outcome.status !== 'busy') window.location.assign(window.routes.cart_url);
        } catch (_error) {
          setError(transactions.text('review_needed'));
          window.location.assign(window.routes.cart_url);
        } finally {
          setBusy(false);
          syncMode();
        }
      },
      true
    );

    syncMode();
  };

  const boot = () => document.querySelectorAll(ROOT_SELECTOR).forEach(createController);
  const syncAll = () =>
    document.querySelectorAll(ROOT_SELECTOR).forEach((root) => root.lnpSyncBundleMode?.());

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
  window.addEventListener('pageshow', syncAll);

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source !== SOURCE) syncAll();
    });
  }
})();
